import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature, Point } from "geojson";
import mapboxgl, { FeatureIdentifier } from "mapbox-gl";

import { useGetVersionQuery } from "fond/api";
import DrawHandler from "fond/draw/DrawHandler";
import { buildPathGraph } from "fond/draw/modes/base/pathGraph";
import { MapContext } from "fond/map/MapProvider";
import { RedoButton, SelectButton, ToolbarConfig, UndoButton } from "fond/map/Toolbar";
import * as turf from "fond/turf";
import { LayerIds } from "fond/types";
import { isAnyLineString, isLineString, isPoint } from "fond/types/geojson";
import { useAppSelector } from "fond/utils/hooks";

import drawAssignment from "./drawAssignment";
import dropDrawStyles from "./dropDrawStyles";
import {
  calculateSourceMaxCableCount,
  createAssignmentCable,
  getCables,
  getEdgeFibreCount,
  getEdgeSinkGroupId,
  getExistingAssignments,
  getSinkGroups,
  getSources,
} from "./helper";
import selectDrop from "./selectDrop";
import SelectionPopup from "./SelectionPopup";
import SourceSelectionPopup from "./SourceSelectionPopup";
import { AssignmentCableFeature, AssignmentCableSystemFeature, CableFeature, SourceFeature } from "./types";

/**
 * Toolbar configuration for drop assignment editing.
 */
const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />],
  },
];

/**
 * Source layers that contain the features being edited.
 */
const sourceLayers: FeatureIdentifier[] = [{ source: LayerIds.fibreCable }, { source: LayerIds.hub }];

type CableSelectState = {
  cables: (CableFeature | AssignmentCableFeature)[];
  lngLat: mapboxgl.LngLatLike;
};

interface CreateAssignmentArgs {
  // The original cable being reassigned
  originalCable: CableFeature;

  // The cable or assignment feature being edited
  selectedCable: CableFeature | AssignmentCableFeature;

  // The source to reassign the selected cable to
  source: SourceFeature;
}

type SourceSelectState = {
  sources: SourceFeature[];
  lngLat: mapboxgl.LngLatLike;
} & Pick<CreateAssignmentArgs, "originalCable" | "selectedCable">;

/**
 * Handler for cable reassignment in Planner Extended projects.
 *
 * This allows users to reassign DROP cables to different sources after the Design phase completes.
 */
const DropAssignmentHandler: React.FC = () => {
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);
  const versionId = useAppSelector((state) => state.project.versionId);
  const { data: version } = useGetVersionQuery(versionId);
  const sourceMaxCableCount = useMemo(() => calculateSourceMaxCableCount(version?.Architecture), [version?.Architecture]);
  const { map, drawControl } = useContext(MapContext);
  const originalVisibilityRef = useRef<Map<string, string>>(new Map());

  const [cableSelectState, setCableSelectState] = useState<CableSelectState | null>(null);
  const [sourceSelectState, setSourceSelectState] = useState<SourceSelectState | null>(null);

  const ugGraph = useMemo(() => {
    // Underground path is splittable (the preprocessor handles this this), but leadins should not be split.
    const inStreetLines = (data?.layers?.[LayerIds.inStreet]?.features ?? []).filter(isAnyLineString).map((f, i) => ({ ...f, id: `instreet-${i}` }));
    const leadinLines = (data?.layers?.["input_layers/leadins"]?.features ?? []).filter(isAnyLineString).map((f, i) => ({ ...f, id: `leadin-${i}` }));
    const leadinIds = new Set(leadinLines.map((f) => f.id));

    const sourcesWithoutCapacity = getSources((data?.layers?.[LayerIds.hub]?.features ?? []).filter(isPoint), [], [], null);
    const sourceLookup = new Map(sourcesWithoutCapacity.map((f) => [f.properties.fondId, f]));

    const sinkGroups = getSinkGroups(
      (data?.layers?.["output/service_location"]?.features ?? []).filter(isPoint),
      (data?.layers?.[LayerIds.fibreCable]?.features ?? []).filter(isLineString),
      sourceLookup
    );
    const dropHubs = (data?.layers?.["output/hub"]?.features ?? []).filter((f): f is Feature<Point> => isPoint(f) && f.properties?.Tier === "DROP");

    return buildPathGraph([...inStreetLines, ...leadinLines], {
      splitIntersectingPaths: true,
      isSplittable: (f) => !leadinIds.has(f.id as string),
      namedNodes: [
        ...dropHubs.map((h) => ({ id: `dropHub:${h.properties?.FondID}`, coord: h.geometry.coordinates })),
        ...sinkGroups.map((g) => ({ id: `slg:${g.properties.groupId}`, coord: g.geometry.coordinates })),
      ],
    });
  }, [data]);

  // Custom drop assignment editing draw modes
  const modes = useMemo(
    () => ({
      select_drop: selectDrop,
      draw_assignment: drawAssignment,
      // DrawHandler requires a `simple_select` mode even if we don't intend to use it
      simple_select: MapboxDraw.modes.simple_select,
    }),
    []
  );

  // Manage layer visibilities during assignment cable editing.
  useEffect(() => {
    if (!map) return;

    const hiddenLayerIds: string[] = [];

    // Get all map layers and determine which should be visible
    map.getStyle()?.layers.forEach((layer) => {
      const layerId = layer.id;

      // Skip Mapbox base layers and GL Draw layers (drawing UI)
      if (
        layerId.startsWith("mapbox-") ||
        layerId.startsWith("gl-draw-") ||
        layerId.startsWith("background") ||
        layerId.startsWith("landcover") ||
        layerId.startsWith("landuse") ||
        layerId.startsWith("water") ||
        layerId.startsWith("building") ||
        layerId.startsWith("road") ||
        layerId.startsWith("admin")
      ) {
        return;
      }

      // Keep only service location layers visible
      const shouldBeVisible = "source" in layer && layer.source === LayerIds.serviceLocation;

      // Hide all other layers (including sources and cables - they're in draw control now)
      if (!shouldBeVisible) {
        const originalVisibility = map.getLayoutProperty(layerId, "visibility") || "visible";
        originalVisibilityRef.current.set(layerId, originalVisibility);
        map.setLayoutProperty(layerId, "visibility", "none");
        hiddenLayerIds.push(layerId);
      }
    });

    const visibility = originalVisibilityRef.current;

    // eslint-disable-next-line consistent-return
    return () => {
      hiddenLayerIds.forEach((layerId) => {
        if (map.getLayer(layerId)) {
          const originalVisibility = visibility.get(layerId) || "visible";
          map.setLayoutProperty(layerId, "visibility", originalVisibility);
        }
      });
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;

    const handleCableSelect = (event: CableSelectState) => {
      setCableSelectState(event);
    };
    const handleSourceSelect = (event: SourceSelectState) => {
      setSourceSelectState(event);
    };

    const handleClose = () => {
      setSourceSelectState(null);
      setCableSelectState(null);
    };

    map.on("drop_assignments.show_cable_selection", handleCableSelect);
    map.on("drop_assignments.hide_cable_selection", handleClose);
    map.on("drop_assignments.show_source_selection", handleSourceSelect);

    // eslint-disable-next-line consistent-return
    return () => {
      map.off("drop_assignments.show_cable_selection", handleCableSelect);
      map.off("drop_assignments.hide_cable_selection", handleClose);
      map.off("drop_assignments.show_source_selection", handleSourceSelect);
    };
  }, [map]);

  const handleCableSelected = useCallback(
    (selection: { cable: CableFeature | AssignmentCableFeature }) => {
      drawControl.current?.changeMode("draw_assignment", { selectedCable: selection.cable });
      setCableSelectState(null);
    },
    [drawControl]
  );

  const handleCablePopupClose = useCallback(() => {
    setCableSelectState(null);
  }, []);

  const completeAssignment = useCallback(
    ({ originalCable, selectedCable, source }: CreateAssignmentArgs) => {
      const draw = drawControl.current;
      if (!draw) return;

      // Update the hub capacities
      const previousSource = selectedCable.properties.source;
      if (previousSource.properties.fondId !== source.properties.fondId) {
        const previousSourceFeature = draw.get(previousSource.id as string) as SourceFeature;
        const sourceFeature = draw.get(source.id as string) as SourceFeature;

        if (
          (previousSourceFeature?.properties?.remainingCapacity ?? null) !== null &&
          (sourceFeature?.properties?.remainingCapacity ?? null) !== null
        ) {
          const count = getEdgeFibreCount(originalCable);
          draw.setFeatureProperty(previousSource.id as string, "remainingCapacity", previousSourceFeature.properties.remainingCapacity! + count);
          draw.setFeatureProperty(source.id as string, "remainingCapacity", sourceFeature.properties.remainingCapacity! - count);
        }
      }

      // Create the new assignment cable feature
      const newAssignment = createAssignmentCable(selectedCable, source, ugGraph);
      draw.delete(selectedCable.id as string);
      draw.add(newAssignment);
      map?.fire("draw.update", { features: [newAssignment] });
      draw.changeMode("select_drop");
    },
    [drawControl, map, ugGraph]
  );

  useEffect(() => {
    map?.on("drop_assignments.complete_assignment", completeAssignment);
    return () => {
      map?.off("drop_assignments.complete_assignment", completeAssignment);
    };
  }, [map, completeAssignment]);

  const handleSourceSelected = useCallback(
    (source: SourceFeature) => {
      if (sourceSelectState) {
        completeAssignment({
          originalCable: sourceSelectState.originalCable,
          selectedCable: sourceSelectState.selectedCable,
          source,
        });
      }
      setSourceSelectState(null);
    },
    [completeAssignment, sourceSelectState]
  );

  const handleSourcePopupClose = () => {
    setSourceSelectState(null);
    drawControl.current?.changeMode("select_drop");
  };

  // Initial features for the draw control
  const initialFeatures: AssignmentCableSystemFeature[] = useMemo(() => {
    if (!data?.layers) return [];

    // First pass: create sources without capacity to establish the source lookup
    const sourcesWithoutCapacity = getSources((data?.layers?.[LayerIds.hub]?.features ?? []).filter(isPoint), [], [], null);
    const sourceLookup = new Map(sourcesWithoutCapacity.map((f) => [f.properties.fondId, f]));
    const rawCables = (data?.layers?.[LayerIds.fibreCable]?.features ?? []).filter(isLineString);
    const sinkGroups = getSinkGroups((data?.layers?.["output/service_location"]?.features ?? []).filter(isPoint), rawCables, sourceLookup);
    const cables = getCables(rawCables, sourceLookup, sinkGroups);
    const existingAssignments = getExistingAssignments(
      (data?.layers?.[LayerIds.dropAssignment]?.features ?? []).filter(isLineString),
      sinkGroups,
      sourcesWithoutCapacity
    );

    // If there's an assignment corresponding to a cable, then don't show that cable (that would be confusing!).
    const assignmentFeatures: AssignmentCableFeature[] = [];
    const seenAssignmentGroupIds = new Set<string>();

    const filteredCables = cables.filter((cable) => {
      const sinkGroupId = getEdgeSinkGroupId(cable);
      // Already observed a cable or assignment for this group
      if (seenAssignmentGroupIds.has(sinkGroupId)) {
        return false;
      }
      seenAssignmentGroupIds.add(sinkGroupId);

      const result = existingAssignments.get(sinkGroupId);
      if (result) {
        assignmentFeatures.push(createAssignmentCable(cable, result.source, ugGraph) as AssignmentCableFeature);
        return false;
      }

      return true;
    });

    const filteredGroups = sinkGroups.filter((group) => seenAssignmentGroupIds.has(group.properties.groupId));

    // Second pass: create sources with correct remaining capacity based on cables AND assignments
    const sources = getSources(
      (data?.layers?.[LayerIds.hub]?.features ?? []).filter(isPoint),
      filteredCables,
      assignmentFeatures,
      sourceMaxCableCount
    );

    const pits = (data?.layers?.[LayerIds.inStreetPoints]?.features ?? []).filter(isPoint).map((f) => turf.feature(f.geometry, { type: "pit" }));

    // sources last so they're on top
    return [...filteredCables, ...assignmentFeatures, ...filteredGroups, ...pits, ...sources];
  }, [data, sourceMaxCableCount, ugGraph]);

  const options: MapboxDrawOptions = useMemo(
    () =>
      ({
        editingLayerIds: [LayerIds.fibreCable],
        snap: false,
        shortestPath: { graph: ugGraph },
      }) as MapboxDrawOptions,
    [ugGraph]
  );

  return (
    <>
      <DrawHandler
        initialFeatures={initialFeatures}
        sources={sourceLayers}
        styles={dropDrawStyles}
        config={toolbarConfig}
        modes={modes}
        startingMode="select_drop"
        options={options}
        disableHoverEffects
      />
      {cableSelectState && (
        <SelectionPopup
          cables={cableSelectState.cables}
          lngLat={cableSelectState.lngLat}
          onSelect={handleCableSelected}
          onClose={handleCablePopupClose}
        />
      )}
      {sourceSelectState && (
        <SourceSelectionPopup
          sources={sourceSelectState.sources}
          lngLat={sourceSelectState.lngLat}
          onSelect={handleSourceSelected}
          onClose={handleSourcePopupClose}
        />
      )}
    </>
  );
};

export default DropAssignmentHandler;
